"use client";

import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { useRef, type PointerEvent, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Extra classes on the wrapper. Defaults to inline-flex so it hugs its child. */
  className?: string;
  /** Max pull toward the cursor, in px, at the edge of the element. */
  strength?: number;
};

/**
 * Magnetic hover wrapper. The wrapped element eases toward the pointer with
 * spring physics and settles back when the pointer leaves — the premium
 * "buttons that reach for you" micro-interaction.
 *
 * Progressive enhancement: only mouse pointers drive it (touch/pen are ignored,
 * mirroring HeroDotGrid), and `prefers-reduced-motion` collapses it to a plain
 * wrapper with no motion values or listeners. The transform is GPU-only
 * (translate), so it never triggers layout or CLS.
 */
export default function Magnetic({ children, className = "inline-flex", strength = 14 }: Props) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 220, damping: 16, mass: 0.3 });
  const springY = useSpring(y, { stiffness: 220, damping: 16, mass: 0.3 });

  function handleMove(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== "mouse") return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    x.set((relX / (rect.width / 2)) * strength);
    y.set((relY / (rect.height / 2)) * strength);
  }

  function reset() {
    x.set(0);
    y.set(0);
  }

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ x: springX, y: springY }}
      onPointerMove={handleMove}
      onPointerLeave={reset}
    >
      {children}
    </motion.div>
  );
}
